import React, { useState, useEffect } from 'react';
import api from '../../api/axiosConfig';
import './PanelPrincipalCliente.css';

function formatRelTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const now = new Date();
  const diffMin = Math.floor((now - date) / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);
  if (diffMin < 1) return 'Ahora';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  if (diffHrs < 24) return `Hace ${diffHrs} hs`;
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} dias`;
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
}

function parseDateSafe(raw) {
  if (!raw) return null;
  const d = new Date(raw.includes('T') ? raw : raw + 'T12:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(raw) {
  const date = parseDateSafe(raw);
  if (!date) return raw || '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((target - today) / 86400000);
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Mañana';
  if (diffDays < 7) return `En ${diffDays} dias`;
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
}

const getRoleColor = (rol) => {
  switch (rol?.toLowerCase()) {
    case 'monotributista': return { bg: '#dcfce7', text: '#166534', border: '#bbf7d0', dot: '#22c55e' };
    case 'autonomo': return { bg: '#fef08a', text: '#854d0e', border: '#fde047', dot: '#eab308' };
    case 'persona juridica': return { bg: '#e0e7ff', text: '#3730a3', border: '#c7d2fe', dot: '#6366f1' };
    case 'sociedad de hecho': return { bg: '#ffedd5', text: '#9a3412', border: '#fed7aa', dot: '#f97316' };
    case 'dependiente': return { bg: '#f3e8ff', text: '#6b21a8', border: '#e9d5ff', dot: '#a855f7' };
    case 'asociacion sin fines de lucro': return { bg: '#ffe4e6', text: '#9f1239', border: '#fecdd3', dot: '#e11d48' };
    default: return { bg: '#f1f5f9', text: '#475569', border: '#e2e8f0', dot: '#64748b' };
  }
};

export default function PanelPrincipalCliente({ user, entidad, onNavigate }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [muroPosts, setMuroPosts] = useState([]);

    useEffect(() => {
        if (!entidad?.id) return;
        setLoading(true);
        api.post('/obtenerDashboardCliente', { entidadId: entidad.id })
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [entidad?.id]);

    useEffect(() => {
        api.get('/muro/obtener', { params: { activo: '1' } })
            .then(res => setMuroPosts(res.data?.data || []))
            .catch(() => {});
    }, []);

    if (!user) return null;
    if (loading) {
        return (
            <div className="dashboard-content">
                <div className="panel-cliente-loading">
                    <div className="panel-cliente-bouncing-loader">
                        <div className="panel-cliente-bounce-ball"></div>
                        <div className="panel-cliente-bounce-ball"></div>
                        <div className="panel-cliente-bounce-ball"></div>
                    </div>
                    <p className="panel-cliente-loading-text">Cargando panel...</p>
                </div>
            </div>
        );
    }

    const entity = data?.entityInfo || entidad || {};
    const entityColors = getRoleColor(entity.rol);
    const displayName = entity.razonSocial || entity.nombre || user.nombre || 'Usuario';
    const initials = displayName.charAt(0).toUpperCase();
    const procedures = data?.activeProcedures || [];
    const reminders = data?.upcomingReminders || [];
    const stats = [
        { label: 'Trámites Activos', value: data?.activeCount || 0, icon: 'bi-clipboard-check', color: '#2a5a8a', bg: '#ebf4ff' },
        { label: 'Completados', value: data?.completadosCount || 0, icon: 'bi-check2-all', color: '#16a34a', bg: '#f0fdf4' },
        { label: 'Pendientes', value: (data?.notificationsPending || 0), icon: 'bi-bell', color: '#d97706', bg: '#fffbeb' },
        { label: 'Próximos', value: reminders.length, icon: 'bi-calendar-event', color: '#7c3aed', bg: '#f5f3ff' },
    ];

    return (
        <div className="dashboard-content">
            <div className="page-header cliente-header-flex">
                <div>
                    <h1 className="page-title">Bienvenido, {user.nombre}</h1>
                    <p className="page-subtitle">Panel de control de tu cuenta</p>
                </div>
                <div className="cliente-entity-badge" style={{ borderColor: entityColors.border, backgroundColor: entityColors.bg }}>
                    <div className="cliente-entity-avatar" style={{ backgroundColor: entityColors.dot }}>
                        {initials}
                    </div>
                    <div className="cliente-entity-info">
                        <span className="cliente-entity-name">{displayName}</span>
                        <span className="cliente-entity-meta">
                            {entity.cuit && <>CUIT: {entity.cuit} · </>}
                            <span style={{ color: entityColors.text }}>{entity.rol || 'Cliente'}</span>
                        </span>
                    </div>
                </div>
            </div>

            <div className="stats-grid">
                {stats.map((s, i) => (
                    <div className="stat-card cliente-stat-card" key={i}>
                        <div className="stat-icon" style={{ backgroundColor: s.bg, color: s.color }}>
                            <i className={`bi ${s.icon}`}></i>
                        </div>
                        <div className="stat-details">
                            <h3 style={{ color: s.color }}>{s.value}</h3>
                            <p>{s.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {muroPosts.length > 0 && (() => {
                const post = muroPosts[0];
                return (
                <div className="cliente-muro-banner">
                    <div className="cliente-muro-banner-icon">
                        <i className="bi bi-megaphone-fill"></i>
                    </div>
                    <div className="cliente-muro-banner-body">
                        <div className="cliente-muro-banner-titulo">{post.titulo}</div>
                        {post.subtitulo && <div className="cliente-muro-banner-sub">{post.subtitulo}</div>}
                        {post.contenido && (
                            <div className="cliente-muro-banner-content" dangerouslySetInnerHTML={{ __html: post.contenido }} />
                        )}
                    </div>
                    <div className="cliente-muro-banner-fecha">
                        {new Date(post.fecha_publicacion).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
                    </div>
                </div>
                );
            })()}

            <div className="panel-cliente-cards-grid">
                <div className="panel-cliente-card panel-cliente-card-tramites">
                    <h3 className="panel-cliente-card-header">
                        <i className="bi bi-clipboard-check" style={{ color: '#2a5a8a' }}></i>Trámites Activos
                        {procedures.length > 0 && <span className="panel-cliente-badge" style={{ background: '#2a5a8a', color: 'white' }}>{procedures.length}</span>}
                    </h3>
                    {procedures.length === 0 ? (
                        <div className="panel-cliente-empty-state">
                            <i className="bi bi-inbox"></i>
                            <p>No hay trámites activos</p>
                        </div>
                    ) : (
                        <>
                            {procedures.slice(0, 3).map(p => (
                                <div key={p.id} className="cliente-proc-item" onClick={() => onNavigate && onNavigate(p.id)}>
                                    <div className="cliente-proc-header">
                                        <span className="cliente-proc-name">{p.plantilla_nombre}</span>
                                        <span className={`cliente-proc-status ${p.estado_general === 'Completado' ? 'done' : 'pending'}`}>
                                            {p.estado_general}
                                        </span>
                                    </div>
                                    <div className="cliente-proc-bar-wrap">
                                        <div className="cliente-proc-bar">
                                            <div className="cliente-proc-bar-fill" style={{ width: `${p.progreso}%` }}></div>
                                        </div>
                                        <span className="cliente-proc-pct">{p.progreso}%</span>
                                    </div>
                                </div>
                            ))}
                            {procedures.length > 3 && (
                                <div className="panel-cliente-resumen-footer">
                                    <button onClick={() => onNavigate && onNavigate()}>
                                        Ver más ({procedures.length - 3} restantes) <i className="bi bi-arrow-right"></i>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="panel-cliente-card panel-cliente-card-reminders">
                    <h3 className="panel-cliente-card-header">
                        <i className="bi bi-calendar-event" style={{ color: '#7c3aed' }}></i>Próximos Recordatorios
                        {reminders.length > 0 && <span className="panel-cliente-badge" style={{ background: '#7c3aed', color: 'white' }}>{reminders.length}</span>}
                    </h3>
                    {reminders.length === 0 ? (
                        <div className="panel-cliente-empty-state">
                            <i className="bi bi-calendar-check"></i>
                            <p>No hay recordatorios próximos</p>
                        </div>
                    ) : (
                        <ul className="cliente-reminder-list">
                            {reminders.map(r => (
                                <li key={r.id} className="cliente-reminder-item">
                                    <div className="cliente-reminder-date-badge">
                                        <span className="cliente-reminder-day">{parseDateSafe(r.fecha)?.getDate() || '?'}</span>
                                        <span className="cliente-reminder-month">
                                            {parseDateSafe(r.fecha)?.toLocaleDateString('es-AR', { month: 'short' }) || ''}
                                        </span>
                                    </div>
                                    <div className="cliente-reminder-body">
                                        <span className="cliente-reminder-title">{r.titulo}</span>
                                        {r.descripcion && <span className="cliente-reminder-desc">{r.descripcion}</span>}
                                        <span className="cliente-reminder-meta">{formatDate(r.fecha)} · {r.hora?.slice(0, 5) || ''}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <div className="panel-cliente-section">
                <h3 className="panel-cliente-section-title">
                    <i className="bi bi-activity" style={{ color: '#2a5a8a' }}></i>Actividad Reciente
                </h3>
                {(!data || !data.recentActivity || data.recentActivity.length === 0) ? (
                    <div className="panel-cliente-empty-state" style={{ padding: '40px 0' }}>
                        <i className="bi bi-inbox"></i>
                        <p>Sin actividad aún</p>
                    </div>
                ) : (
                    <div className="cliente-timeline">
                        {data.recentActivity.map((item, idx) => {
                            const info = {
                                tramite_iniciado: { color: '#2a5a8a', icon: 'bi-play-circle', bg: '#ebf4ff' },
                                tramite_completado: { color: '#16a34a', icon: 'bi-check2-circle', bg: '#f0fdf4' }
                            }[item.tipo] || { color: '#64748b', icon: 'bi-record', bg: '#f1f5f9' };
                            const isLast = idx === data.recentActivity.length - 1;
                            return (
                                <div className="cliente-timeline-item" key={idx}>
                                    <div className="cliente-timeline-line">
                                        <div className="cliente-timeline-dot" style={{ backgroundColor: info.color }}></div>
                                        {!isLast && <div className="cliente-timeline-bar" style={{ backgroundColor: '#e2e8f0' }}></div>}
                                    </div>
                                    <div className="cliente-timeline-card" style={{ borderLeftColor: info.color }}>
                                        <div className="cliente-timeline-card-icon" style={{ backgroundColor: info.bg, color: info.color }}>
                                            <i className={`bi ${info.icon}`}></i>
                                        </div>
                                        <div className="cliente-timeline-card-body">
                                            <span className="cliente-timeline-text">{item.descripcion}</span>
                                            <span className="cliente-timeline-time">{formatRelTime(item.fecha)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}